import { Button, Dropdown, Icon, Input, Menu, Spin, Modal } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useQueryClient } from 'react-query';
import useDebouncedSearch from '../../../../../hooks/useDebouncedSearch';
import useFacility from '../../../queries/useFacility';
import useFacilityMeters from '../../../queries/useFacilityMeters';
import useMeterTypes from '../../../queries/useMeterTypes';
import MeterModal from '../../modals/MeterModal/MeterModal';
import LayeredTable from '../layeredTable/LayeredTable/LayeredTable';
import MetersExpandedRow from '../../../meters/MetersExpandedRow/MetersExpandedRow';
import queries from '../../../queries/queries';
import deleteMeter from '../../../requests/deleteMeters';
import { facilityMetersKeys, roomQueryKeys } from '../../../queries/queryKeys';
import styles from './MetersTable.module.scss';

const properties = [
  { name: 'meterCode', header: 'Номер счетчика', flex: 1, highlight: true },
  { name: 'type.name', header: 'Тип', flex: 1 },
  { name: 'coefficient', header: 'Коэффициент', flex: 1 },
  { name: 'rentalHouseTariff.value', header: 'Тариф', flex: 1 },
  { name: 'lastReading', header: 'Последнее показание', flex: 1 },
  { name: 'currentReading', header: 'Текущее показание', flex: 1 },
];

const { confirm } = Modal;

const MetersTable = ({ lightTheme = true, setQueryKeys }) => {
  const queryClient = useQueryClient();
  const { CanCreateOffice, CanDeleteOffice } = useSelector(
    (store) => store.authentication.userInfo
  );

  const { facilityId, facilityTab, roomId, roomTab } = useParams();

  const [search, setSearch, debouncedSearch] = useDebouncedSearch();

  const [selectedMeterTypeId, setSelectedMeterTypeId] = useState();
  const [selectedMeterTypeName, setSelectedMeterTypeName] = useState('');
  const [initialValues, setInitialValues] = useState(null);
  const [isMeterModalVisible, setIsMeterModalVisible] = useState(false);

  const { data: facility } = useFacility(facilityId);

  const office = queryClient.getQueryData(roomQueryKeys(roomId));

  const { data: facilityMeters, isFetching: isFacilityMetersFetching } =
    useFacilityMeters(facilityId, roomId, debouncedSearch, selectedMeterTypeId);

  const { data: meterTypes } = useMeterTypes();

  useEffect(() => {
    if (typeof setQueryKeys === 'function') {
      setQueryKeys((prev) =>
        prev.concat([
          facilityMetersKeys({
            facilityId,
            officeId: roomId,
            searchString: debouncedSearch,
            typeId: selectedMeterTypeId,
          }),
        ])
      );
    }
  }, [facilityId, roomId, debouncedSearch, selectedMeterTypeId, setQueryKeys]);

  const updateMeters = () =>
    queryClient.invalidateQueries(queries.facilityMeters);

  const openMeterModal = useCallback(() => setIsMeterModalVisible(true), []);

  const closeMeterModal = useCallback(() => {
    setIsMeterModalVisible(false);
    setInitialValues(null);
  }, []);

  const handleMenuClick = useCallback((e) => {
    setSelectedMeterTypeId(+e.key);
    setSelectedMeterTypeName(e.item.props.children);
  }, []);

  const onSearchChange = useCallback(
    (e) => setSearch(e.target.value),
    [setSearch]
  );

  const meterTypesMenu = useMemo(
    () => (
      <Menu onClick={handleMenuClick}>
        <Menu.Item key={null}>Все типы</Menu.Item>
        {meterTypes?.map((meterType) => (
          <Menu.Item key={meterType.id}>{meterType.name}</Menu.Item>
        ))}
      </Menu>
    ),
    [meterTypes, handleMenuClick]
  );

  const handleEditRow = useCallback((_, rowData) => {
    setIsMeterModalVisible(true);
    setInitialValues(rowData);
  }, []);

  const handleDeleteRow = (meter) => {
    confirm({
      title: `Вы уверены, что хотите удалить счетчик ${meter.meterCode || ''}?`,
      content: 'Это действие нельзя будет отменить',
      onOk: async () => {
        const res = await deleteMeter(meter.id);

        if (res.result === 'Ok') {
          updateMeters();
        }
      },
      onCancel() {},
    });
  };

  useEffect(() => {
    if (facilityTab !== 'meters' || roomTab !== 'meters') {
      setSearch('');
      setSelectedMeterTypeId();
      setSelectedMeterTypeName('');
    }
  }, [facilityTab, roomTab, setSearch]);

  return (
    <Spin spinning={isFacilityMetersFetching}>
      <div className={styles.metersLayout}>
        <div className={styles.menu}>
          <div className={styles.leftSide}>
            <Input
              value={search}
              onChange={onSearchChange}
              placeholder={'Номер счетчика'}
              suffix={<Icon type="search" />}
              className={styles.searchInput}
            />
            <Dropdown overlay={meterTypesMenu} trigger={['click']}>
              <Button shape={'round'} className={styles.chooseDropdown}>
                {selectedMeterTypeName || 'Типы счётчиков'} <Icon type="down" />
              </Button>
            </Dropdown>
          </div>
          {CanCreateOffice && (
            <Button className={styles.addButton} onClick={openMeterModal}>
              Добавить счётчик
            </Button>
          )}
        </div>
        <LayeredTable
          onEditRow={handleEditRow}
          onDeleteRow={handleDeleteRow}
          data={facilityMeters || []}
          lightTheme={lightTheme}
          rowDataPropertiesArray={properties}
          expandable="start"
          editable={CanCreateOffice}
          deletable={CanDeleteOffice}
          expandedRowContent={(meter) => <MetersExpandedRow meter={meter} />}
        />
      </div>
      {isMeterModalVisible && (
        <MeterModal
          visible={isMeterModalVisible}
          facilityData={facility}
          officeData={office}
          initialValues={initialValues}
          onClose={closeMeterModal}
          onOk={updateMeters}
        />
      )}
    </Spin>
  );
};

export default MetersTable;
